import React, { useEffect, useState } from 'react';
import { DatePicker, InputNumber, Modal, Radio, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { OrderQuotaRow } from '../graphql/orderQuotaGraphql';
import { MAX_QUOTA, formatQuota } from '../utils/orderQuotaGrid';

const { RangePicker } = DatePicker;

interface BatchOverrideModalProps {
    open: boolean;
    row?: OrderQuotaRow;
    /** 美西今日 yyyy-MM-dd，开始日期不得早于今日 */
    today: string;
    /** 不能超过的值（低优上限 ≤ 基础产能） */
    maxValue?: number;
    onCancel: () => void;
    onSubmit: (startDate: string, endDate: string, dailyQuota: number | null) => Promise<void>;
}

/**
 * 按日批量设置 / 清除弹窗：清除即恢复为行日产能。
 */
const BatchOverrideModal: React.FC<BatchOverrideModalProps> = ({ open, row, today, maxValue, onCancel, onSubmit }) => {
    const { t } = useTranslation();
    const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
    const [mode, setMode] = useState<'set' | 'clear'>('set');
    const [quota, setQuota] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setRange(null);
            setMode('set');
            setQuota(null);
        }
    }, [open]);

    const days = range ? range[1].diff(range[0], 'day') + 1 : 0;
    const invalidQuota = mode === 'set' && (quota === null || (maxValue !== undefined && quota > maxValue));

    const handleOk = async () => {
        if (!range || invalidQuota) return;
        setSubmitting(true);
        try {
            await onSubmit(range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'), mode === 'set' ? quota : null);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            title={t('central.orderQuota.batch.title', { name: row?.targetName ?? '' })}
            okText={t('common.confirm')}
            cancelText={t('common.cancel')}
            okButtonProps={{ disabled: !range || invalidQuota, loading: submitting }}
            onCancel={onCancel}
            onOk={handleOk}
            destroyOnClose
        >
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <span>{t('central.orderQuota.batch.range')}</span>
                    <RangePicker
                        value={range}
                        disabledDate={(date) => date.format('YYYY-MM-DD') < today}
                        onChange={(value) => setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)}
                    />
                    {days > 0 && (
                        <Typography.Text type="secondary">{t('central.orderQuota.batch.days', { days })}</Typography.Text>
                    )}
                </div>
                <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
                    <Radio value="set">{t('central.orderQuota.batch.set')}</Radio>
                    <Radio value="clear">{t('central.orderQuota.batch.clear')}</Radio>
                </Radio.Group>
                {mode === 'set' && (
                    <div className="flex flex-col gap-1">
                        <InputNumber
                            className="w-48"
                            min={0}
                            max={MAX_QUOTA}
                            precision={0}
                            value={quota}
                            status={invalidQuota && quota !== null ? 'error' : undefined}
                            onChange={(value) => setQuota(value === null ? null : Number(value))}
                        />
                        <Typography.Text type="secondary">
                            {t('central.orderQuota.batch.hint', {
                                rowQuota: row?.dailyQuota === null || row?.dailyQuota === undefined ? '-' : formatQuota(row.dailyQuota),
                            })}
                            {maxValue !== undefined && t('central.orderQuota.batch.maxHint', { max: formatQuota(maxValue) })}
                            {t('central.orderQuota.zeroReject')}
                        </Typography.Text>
                    </div>
                )}
            </div>
        </Modal>
    );
};


export default BatchOverrideModal;
